/**
 * 🐥 April Fools gag
 * Buttons dodge the cursor when you get close, then a banner appears with a kill switch.
 *
 * LS states: null (first visit) / "on" / "off" / "hide"
 * - null or "on": dodge active, banner shows after 3rd dodge
 * - "off": dodge disabled, banner still shows immediately
 * - "hide": everything disabled permanently
 */
import React from 'react'
import { createRoot } from 'react-dom/client'
import i18n from 'i18next'
import { Switch, Button } from '@duck/ui'

type AprilState = 'on' | 'off' | 'hide' | null

export class AprilFools {
  private static readonly STORAGE_KEY = 'april-fools'
  private static readonly DODGE_DISTANCE = 120
  private static readonly DODGE_SELECTORS = 'button, a[role="button"], [role="tab"], a[href]'
  private static readonly DODGE_COOLDOWN = 2500
  private static readonly MOBILE_BREAKPOINT = 768

  private dodgeAborted!: boolean
  private firstDodgeFired = 0
  private bannerShown = false

  constructor() {
    if (this.isMobile()) return

    const state = this.getState()
    if (state === 'hide') return

    this.dodgeAborted = state === 'off'

    window.addEventListener('resize', () => {
      const banner = document.getElementById('april-fools-banner')
      if (this.isMobile()) {
        this.dodgeAborted = true
        if (banner) banner.style.display = 'none'
      } else {
        this.dodgeAborted = this.getState() === 'off'
        if (banner) banner.style.display = ''
      }
    })

    this.initDodge()

    if (state === 'off') {
      this.bannerShown = true
      this.showBanner()
    }
  }

  private isMobile() {
    return window.innerWidth < AprilFools.MOBILE_BREAKPOINT
  }

  private getState(): AprilState {
    return localStorage.getItem(AprilFools.STORAGE_KEY) as AprilState
  }

  private setState(v: 'on' | 'off' | 'hide') {
    localStorage.setItem(AprilFools.STORAGE_KEY, v)
  }

  private showBanner() {
    const container = document.createElement('div')
    container.id = 'april-fools-banner'
    if (this.getState() !== null) {
      container.classList.add('april-no-wiggle')
    }
    document.body.appendChild(container)

    const root = createRoot(container)
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this

    const Banner = () => {
      const [enabled, setEnabled] = React.useState(!self.dodgeAborted)
      const [visible, setVisible] = React.useState(true)
      const [interacted, setInteracted] = React.useState(false)

      const stopWiggle = () => {
        if (!interacted) {
          setInteracted(true)
          container.classList.add('april-no-wiggle')
        }
      }

      const handleToggle = (checked: boolean) => {
        stopWiggle()
        setEnabled(checked)
        self.dodgeAborted = !checked
        self.setState(checked ? 'on' : 'off')
      }

      const handleHideForever = () => {
        stopWiggle()
        self.dodgeAborted = true
        self.setState('hide')
        setVisible(false)
      }

      if (!visible) return null

      return React.createElement(
        'div',
        {
          style: {
            position: 'fixed',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '700px',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '6px 20px',
            background: 'hsl(var(--color-card))',
            border: '1px solid hsl(var(--color-border))',
            borderTop: 'none',
            borderRadius: '0px 0px 8px 8px',
            fontSize: '1.125rem',
            fontWeight: 600,
            color: 'hsl(var(--color-card-foreground))',
          },
        },
        React.createElement('span', null, i18n.t('aprilFools.message')),
        React.createElement(
          'label',
          { style: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.875rem', cursor: 'pointer' } },
          React.createElement(Switch, {
            checked: enabled,
            onCheckedChange: handleToggle,
          }),
          React.createElement('span', null, enabled ? i18n.t('aprilFools.on') : i18n.t('aprilFools.off'))
        ),
        React.createElement(Button, {
          variant: 'ghost',
          size: 'sm',
          onClick: handleHideForever,
        }, i18n.t('aprilFools.hideForever'))
      )
    }

    root.render(React.createElement(Banner))
  }

  private initDodge() {
    let lastDodgeTime = 0

    document.addEventListener('mousemove', (e) => {
      if (this.dodgeAborted) return

      const now = Date.now()
      if (now - lastDodgeTime < 100) return
      lastDodgeTime = now

      const elements = document.querySelectorAll<HTMLElement>(AprilFools.DODGE_SELECTORS)
      for (const el of elements) {
        if (el.closest('#april-fools-banner')) continue
        if (el.dataset.dodging === 'true') continue
        const rect = el.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) continue

        const elCenterX = rect.left + rect.width / 2
        const elCenterY = rect.top + rect.height / 2
        const dx = elCenterX - e.clientX
        const dy = elCenterY - e.clientY
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist < AprilFools.DODGE_DISTANCE && dist > 0) {
          const angle = Math.atan2(dy, dx)
          const dodgeX = Math.cos(angle) * (AprilFools.DODGE_DISTANCE - dist + 30)
          const dodgeY = Math.sin(angle) * (AprilFools.DODGE_DISTANCE - dist + 30)

          el.classList.add('april-dodge')
          el.style.transform = `translate(${dodgeX}px, ${dodgeY}px)`
          el.dataset.dodging = 'true'

          if (!this.bannerShown && ++this.firstDodgeFired === 3) {
            this.bannerShown = true
            setTimeout(() => this.showBanner(), 2500)
          }

          setTimeout(() => {
            el.style.transform = ''
            setTimeout(() => {
              el.dataset.dodging = 'false'
              el.classList.remove('april-dodge')
            }, 300)
          }, AprilFools.DODGE_COOLDOWN)
        }
      }
    })
  }
}
